import React from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "../../../utils/country";
import { AnnualRenewalPeriod, PlanInterval, SubscriptionPlan } from "../../payment/SubscriptionApi";
import { ISelected } from "./SubcriptionPlans";
import { useContentfulContext } from "../../../context/ContentfulContext";
import { OnlyIf } from "../../common/OnlyIf";

export const DetailSubscriptionPlanItem = React.memo(
  (props: {
    isSignup: boolean;
    currentPlanId?: string;
    subscriptionPlan: SubscriptionPlan & ISelected;
    onSelect: () => void;
    onSubmitBtnClick: (data: any, plan?: SubscriptionPlan) => void;
  }) => {
    const { t, ready } = useTranslation();
    const { content } = useContentfulContext();
    const { isSignup, subscriptionPlan, currentPlanId, onSelect, onSubmitBtnClick } = props;

    const currency = getCurrencySymbol(subscriptionPlan.currency);
    const monthlyAnnualPlanAmount =
      subscriptionPlan.interval === PlanInterval.year && subscriptionPlan.renewalPeriod
        ? Math.floor(subscriptionPlan.amount / (subscriptionPlan.renewalPeriod * 12))
        : 0;

    const planDetails: string[] = content?.subscriptionPlans?.planDetails || [];
    /* `selected` alone, deliberately. The container marks the current plan as
       selected on load and then reassigns `selected` exclusively on every
       click, so this needs no help — and OR-ing the current plan's id back in
       meant the current plan kept the selected treatment after the user picked
       a different one, leaving two cards looking chosen at once. Being the
       current subscription is `current-plan` below; it is not a selection. */
    const isSelected = subscriptionPlan.selected;

    if (!ready) {
      return null;
    }

    const badgeText = subscriptionPlan.subtitle || (subscriptionPlan.amount === 0 ? "Limited time offer" : undefined);

    return (
      <Card
        className={`subscription-card ${isSelected ? "selected-plan" : ""} ${
          subscriptionPlan.stripePlanId === currentPlanId ? "current-plan" : ""
        }`}
        onClick={onSelect}
      >
        <Card.Body className="subscription-card-inner">
          {badgeText && (
            <div className="plan-badge-row">
              <span className="plan-badge">{badgeText}</span>
            </div>
          )}

          <div className="plan-header-block">
            <h3 className="plan-name-title">
              {subscriptionPlan.title || subscriptionPlan.name || "Standard Membership"}
            </h3>
            {/* The US API reports its monthly plans with `renewalPeriod: 0` --
                outside the enum the type declares, which `?? 1` does not catch
                either (0 is not nullish) -- so this line read "0 month". A zero
                renewal period is no renewal period: drop the line rather than
                print one. Only the monthly branch is guarded; the year branch is
                shared with the UK checkout modal and is left exactly as it was. */}
            {(subscriptionPlan.interval === PlanInterval.year || (subscriptionPlan.renewalPeriod ?? 1) > 0) && (
              <div className="plan-renewal-label" data-cy="plan-renewal">
                {subscriptionPlan.interval === PlanInterval.year
                  ? `${subscriptionPlan.renewalPeriod * 12 ?? 12} ${t("subscription.subscriptionInfo.intervalMonths")}`
                  : `${subscriptionPlan.renewalPeriod ?? 1} ${t("subscription.subscriptionInfo.intervalMonth")}`}
              </div>
            )}
          </div>

          <div className="plan-divider" />

          <div className="plan-price-block" data-cy="plan-price">
            {subscriptionPlan.interval === PlanInterval.month ? (
              <div className="price-row">
                <span className="currency-symbol">{currency}</span>
                <span className="price-number">{subscriptionPlan.amount}</span>
                <span className="interval-text">/{subscriptionPlan.interval}</span>
              </div>
            ) : (
              <div className="price-row">
                <span className="currency-symbol">{currency}</span>
                <span className="price-number">{monthlyAnnualPlanAmount}</span>
                <span className="interval-text">/{t("subscription.subscriptionInfo.intervalMonth")}</span>
              </div>
            )}
          </div>

          <div className="plan-billed-as" data-cy="plan-billed-as">
            {subscriptionPlan.interval === PlanInterval.month && (
              <>
                <span className="billed-label">{t("subscription.subscriptionInfo.billed")}</span>{" "}
                <span className="billed-period">{t("subscription.subscriptionInfo.intervalMonthly")}</span>
              </>
            )}
            {subscriptionPlan.interval === PlanInterval.year && (
              <>
                <span className="billed-label">
                  {t("subscription.subscriptionInfo.billedAs")} {currency}
                  {subscriptionPlan.amount}
                </span>{" "}
                <span className="billed-period">
                  {subscriptionPlan.renewalPeriod === AnnualRenewalPeriod.biannually
                    ? t("subscription.subscriptionInfo.intervalBiannual")
                    : t("subscription.subscriptionInfo.intervalAnnual")}
                </span>
              </>
            )}
          </div>

          {subscriptionPlan.creditAmount > 0 && (
            <div className="plan-credit-callout">
              <div className="credit-callout-icon">✓</div>
              <div className="credit-callout-text">
                Eligible for up to{" "}
                <strong>
                  {currency}
                  {subscriptionPlan.creditAmount}
                </strong>{" "}
                credit line
              </div>
            </div>
          )}

          {/* Integrated Benefits for Signup */}
          {isSignup && (
            <div className="plan-benefits-compact mt-3">
              <div className="compact-benefit-row">
                <span className="compact-check">✓</span>
                <span className="compact-text">Shop now, pay later across the Bits store</span>
              </div>
              <div className="compact-benefit-row">
                <span className="compact-check">✓</span>
                <span className="compact-text">Instant approval • No hard credit checks</span>
              </div>
              <div className="compact-benefit-row">
                <span className="compact-check">✓</span>
                <span className="compact-text">0% interest and zero hidden fees</span>
              </div>
              <div className="compact-benefit-row">
                <span className="compact-check">✓</span>
                <span className="compact-text">Build credit with major credit bureaus</span>
              </div>
            </div>
          )}

          <OnlyIf condition={planDetails?.length > 0}>
            <div className="plan-details-list" data-cy="plan-details">
              <ul>
                {planDetails.map((planDetailsText, idx) => (
                  <li key={`planDetails${idx}`}>
                    {planDetailsText.replace(
                      "%CREDIT_LIMIT%",
                      `${getCurrencySymbol(subscriptionPlan?.currency)}${subscriptionPlan?.creditAmount ?? "0"}`
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </OnlyIf>

          {/* Primary Action Button */}
          <div className="plan-card-action mt-4">
            {isSignup ? (
              <>
                <Button
                  type="button"
                  id="sign-up-subscription-plans-submit"
                  disabled={!subscriptionPlan.selected}
                  size="lg"
                  className="w-100 plan-cta-btn"
                  variant="primary"
                  /* The card's own plan travels with the click. The button sits
                     inside the card, so this fires before the card's onClick has
                     bubbled and re-selected it; a handler that read selection
                     state here would submit the previous selection instead. */
                  onClick={(e) => onSubmitBtnClick(e, subscriptionPlan)}
                >
                  {t("settings.forms.plans.choosePlan")}
                </Button>
                <div className="plans-reassurance-row mt-2 text-center">
                  <span className="plans-reassurance-text">✓ No hard credit checks • Cancel anytime</span>
                </div>
              </>
            ) : (
              <div className="d-md-none">
                <Button
                  id={`changePlan${subscriptionPlan.subscriptionPlanId}`}
                  className="w-100"
                  variant="primary"
                  disabled={subscriptionPlan?.stripePlanId === currentPlanId || !subscriptionPlan.selected}
                  onClick={(e) => onSubmitBtnClick(e, subscriptionPlan)}
                >
                  {subscriptionPlan?.stripePlanId === currentPlanId
                    ? t("subscription.forms.planChange.currentPlan")
                    : t("subscription.forms.planChange.changeButton")}
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  }
);
